import { useState } from "react";
import { Phone, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import Reveal from "./Reveal";
import SectionHeader from "./SectionHeader";
import { useLanguage } from "../context/LanguageContext";
import { BUSINESS, waLink } from "../data/siteConfig";
import { WhatsAppIcon } from "./WhatsAppIcon";

const inputClass =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-taupe/60 focus:border-bronze focus:ring-2 focus:ring-bronze/20";

const ConsultationForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    country: "",
    phone: "",
    service: "",
    notes: "",
    consent: false,
  });

  const set = (key) => (e) =>
    setForm((f) => ({
      ...f,
      [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.country || !form.phone || !form.service || !form.consent) {
      toast.error(t.form.requiredNote);
      return;
    }
    const lines = [
      t.form.messageIntro,
      "",
      `${t.form.labels.name}: ${form.name}`,
      `${t.form.labels.country}: ${form.country}`,
      `${t.form.labels.phone}: ${form.phone}`,
      `${t.form.labels.service}: ${form.service}`,
      form.notes ? `${t.form.labels.notes}: ${form.notes}` : null,
    ].filter(Boolean);
    window.open(waLink(lines.join("\n")), "_blank", "noopener,noreferrer");
    toast.success(t.form.toast);
  };

  return (
    <section id="contact" data-testid="consultation-section" className="bg-sand py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <SectionHeader
            eyebrow={t.form.eyebrow}
            heading={t.form.heading}
            sub={t.form.copy}
          />
          <Reveal delay={0.15} className="mt-10">
            <div className="flex flex-col gap-5">
              <a
                href={`tel:+${BUSINESS.whatsappNumber}`}
                data-testid="form-phone-link"
                className="group inline-flex w-fit items-center gap-3 text-ink transition-colors duration-300 hover:text-bronze-dark"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-bronze-dark shadow-sm transition-colors duration-300 group-hover:bg-bronze group-hover:text-white">
                  <Phone className="h-5 w-5" />
                </span>
                <span className="flex flex-col">
                  <span className="text-xs text-taupe">{t.form.callLabel}</span>
                  <span className="font-semibold" dir="ltr">{BUSINESS.phoneDisplay}</span>
                </span>
              </a>
              <div
                data-testid="form-sensitive-note"
                className="flex items-start gap-3 rounded-2xl border border-line bg-white/70 p-5"
              >
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-bronze-dark" />
                <p className="text-xs md:text-sm leading-relaxed text-taupe">
                  {t.form.sensitiveNote}
                </p>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="consultation-form"
            className="rounded-2xl border border-line bg-white p-8 shadow-[0_8px_30px_rgba(28,27,26,0.05)] md:p-10"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <label htmlFor="cbc-name" className="text-sm font-semibold text-ink">
                  {t.form.labels.name} *
                </label>
                <input
                  id="cbc-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.form.placeholders.name}
                  data-testid="form-name-input"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="cbc-country" className="text-sm font-semibold text-ink">
                  {t.form.labels.country} *
                </label>
                <input
                  id="cbc-country"
                  type="text"
                  required
                  value={form.country}
                  onChange={set("country")}
                  placeholder={t.form.placeholders.country}
                  data-testid="form-country-input"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="cbc-phone" className="text-sm font-semibold text-ink">
                  {t.form.labels.phone} *
                </label>
                <input
                  id="cbc-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={t.form.placeholders.phone}
                  data-testid="form-phone-input"
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="cbc-service" className="text-sm font-semibold text-ink">
                  {t.form.labels.service} *
                </label>
                <select
                  id="cbc-service"
                  required
                  value={form.service}
                  onChange={set("service")}
                  data-testid="form-service-select"
                  className={`${inputClass} appearance-none ${form.service ? "" : "text-taupe/60"}`}
                >
                  <option value="" disabled>
                    {t.form.placeholders.service}
                  </option>
                  {t.form.serviceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2 sm:col-span-2">
                <label htmlFor="cbc-notes" className="text-sm font-semibold text-ink">
                  {t.form.labels.notes}
                </label>
                <textarea
                  id="cbc-notes"
                  rows={4}
                  value={form.notes}
                  onChange={set("notes")}
                  placeholder={t.form.placeholders.notes}
                  data-testid="form-notes-input"
                  className={`${inputClass} resize-none`}
                />
              </div>
            </div>

            <label
              htmlFor="cbc-consent"
              className="mt-6 flex cursor-pointer items-start gap-3 text-sm text-taupe"
            >
              <input
                id="cbc-consent"
                type="checkbox"
                checked={form.consent}
                onChange={set("consent")}
                data-testid="form-consent-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-bronze"
              />
              <span>{t.form.consent}</span>
            </label>

            <button
              type="submit"
              data-testid="form-submit-button"
              className="group mt-8 inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-bronze px-7 py-4 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(184,151,104,0.35)] transition-colors duration-300 hover:bg-bronze-dark sm:w-auto"
            >
              <WhatsAppIcon className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
              {t.form.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

export default ConsultationForm;
